import "./Header.css"
import React, {useEffect, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { AppDispatch } from "../../../redux/store";
import { logout } from "../../../redux/actions/user.actions";
import Logo from "../../Layout/Logo";
import {IconButton, Drawer, Button} from '@mui/material';
import RootState from "../../../redux/states/root.state";
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ExternalLinkIcon } from "../Icons/Icons";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import useRoleCheck from "../../../hooks/useRoleCheck";
import { fetchDealership } from "../../../redux/actions/dealership.actions";
import NavigationDrawer from "./NavigationDrawer";
import { localizeSpa } from "../../../utils/brand";
import { MQTT_PREFIX } from "../../../services/mqtt/mqttconfig";
import { getBackTarget } from "./backTargets";


const Header = ({ 
  withBackButton = true, 
  withNavBtn = true, 
  withLogo = true, 
  withUserAvatar=true,
  withBetaLink = true, 
  withShopLink = false,
  header="",
  // Shown right after the page title (e.g. summary counts)
  headerExtra=undefined as React.ReactNode,
  logo='arctic'
}) => {
  const dispatch: AppDispatch = useDispatch();
  const {data:user} = useSelector((state: RootState) => state.user);
  const {data:dealership} = useSelector((state: RootState) => state.dealership);
  const [toggled, setToggled] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const {canAccess:dealerPortalRedirect} = usePermissionCheck(["can_see_dealer_portal_link"]);
  const {canAccess:isCustomer} = useRoleCheck(["customer"]);

  const navigate = useNavigate();
  const location = useLocation();
  const backTarget = getBackTarget(location.pathname, isCustomer);

  const handleBack = () => {
    if (backTarget) navigate(backTarget);
    else navigate(-1);
  };
  

  useEffect(() => {
    if (withShopLink && user?.dealer_id) {
      dispatch(fetchDealership(user.dealer_id));
    }
  }, [dispatch, user]);

  // Lift the bar off the page once content scrolls under it
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleToggle = (isToggled:boolean) => {
    setToggled(isToggled);
  };

  const isBeta = MQTT_PREFIX.includes('beta');

  const isColdTub = (() => {
    const brand = localStorage.getItem('data-brand');
    if (brand) return brand === 'coldtub';
    return window.location.hostname.includes('mycoldtub');
  })();

  const redirectDomain = isColdTub ? 'mycoldtub.com' : 'myarcticspa.com';

  const redirectSiteUrl = isBeta
  ? `https://beta.${redirectDomain}/Spa/MySpa.aspx?ignore_redirect=true`
  : `https://${redirectDomain}/Spa/MySpa.aspx?ignore_redirect=true`;

  // "(Beta)"/"(Classic)" is dropped on small phones (see .header-link-tag)
  const redirectChannel = isBeta ? "Beta" : "Classic";

  const redirectDealerUrl = isBeta
  ? `https://beta.${redirectDomain}/dealers?ignore_redirect=true`
  : `https://${redirectDomain}/dealers?ignore_redirect=true`;


  return (
    <>
      <div className={`header${scrolled ? " scrolled" : ""}`}>
        <div className="right-group">

          <div className="back-button">
            {withBackButton && (
            <IconButton aria-label="Go back" className="header-icon-btn" onClick={handleBack}>
              <ArrowBackIcon/>
            </IconButton>
            )}
          </div>

          {withNavBtn && 
          <div className="back-button">
            <IconButton aria-label="Open menu" className="header-icon-btn" onClick={()=>handleToggle(true)}>
              <MenuIcon/>
            </IconButton>
          </div>}

        </div>

        <Drawer open={toggled} onClose={()=>handleToggle(false)} PaperProps={{sx: {backgroundColor: "var(--pop-over)"}}}>
          <nav className="nav-drawer nav-header">
            <NavigationDrawer/>
          </nav>
        </Drawer>

        {withLogo &&
        <div className="logo">
          <Logo logo={logo}/>
        </div>
        }

      {(withNavBtn) &&
        <div className="right-buttons">
        {withShopLink && user?.dealer_id && dealership?.supplies_url &&
          <Button component="a" className="logout supplies" href={dealership.supplies_url} size="small">
            Buy Supplies
          </Button>
        }
        {withBetaLink && dealerPortalRedirect &&
          <Button component="a" className="logout redirect" href={redirectDealerUrl} size="small">
            Dealer<span className="header-link-tag"> ({redirectChannel})</span><ExternalLinkIcon/>
          </Button>
        }

        {withBetaLink &&
          <Button component="a" className="logout redirect redirect-site" href={redirectSiteUrl} size="small">
            My Spa<span className="header-link-tag"> ({redirectChannel})</span><ExternalLinkIcon/>
          </Button>
        }
        {user ?
            <Button onClick={()=>handleLogout()} className={`logout logout-btn${withBetaLink ? " hide-logout-mobile" : ""}`} size="small">
              Logout
            </Button>
            :
            <Button onClick={()=>handleLogin()} className="logout logout-btn" size="small">
              Login
            </Button>
          }
        </div>
        }
      </div>
      {header !== "" &&
        <div className={`page-title-bar${headerExtra ? " has-extra" : ""}`}>
          <h2>{localizeSpa(header)}</h2>
          {headerExtra}
        </div>
      }
    </>
  );
};

export default Header;
